import { useCallback, useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import debounce from 'lodash.debounce';
import { setIn, useFormik, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LoadingSpinner, ModalPopup, Stack } from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import { LastSaved } from 'modules/notes/notePopup/noteView/addendumForm/lastSaved';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { getClient, postUpdateSnapshot } from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getOtherScreenerDetails,
  getOtherScreenerHistory,
  getScreenerDetails,
  getScreenerHistory,
  getScreenerQuestions,
  getScreeners
} from 'store/screeener/screenerActions';
import { ScreenerTypeLowercase } from 'store/screeener/screenerSlice';
import { ScreenerModal } from 'pages/clients/clientForm/profileInfo/screener/screener';

import { NoteFormValues } from '../utils/validation';
import style from './MpiNoteActions.module.scss';

const MPI_NOTE_TYPES = [
  'MEET_THE_PERSON_INTERVIEW',
  'MEET_THE_PERSON_INTERVIEW_CHILD',
  'MEET_THE_PERSON_INTERVIEW_TEEN'
];

export const isMpiNoteType = (noteType: string | null | undefined) => {
  return noteType != null && MPI_NOTE_TYPES.includes(noteType);
};

type SnapshotModalProps = {
  clientId: string;
  open: boolean;
  onClose: () => void;
};

function SnapshotModal({ clientId, open, onClose }: SnapshotModalProps) {
  const dispatch = useAppDispatch();
  const client = useAppSelector((state) => state.clients.client.data);
  const { organizationId } = getLsUserRoleObject();
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const snapshot = client?.id?.toString() === clientId ? client?.snapshot ?? '' : '';

  const formik = useFormik({
    initialValues: {
      snapshot
    },
    enableReinitialize: true,
    onSubmit: () => {}
  });

  const autosaveForm = useCallback(
    async (values: { snapshot: string }) => {
      if (!clientId || !organizationId) return;

      setIsAutosaving(true);
      const response = await dispatch(
        postUpdateSnapshot({
          snapshot: values.snapshot,
          clientId,
          organizationId
        })
      );

      setIsAutosaving(false);
      if (response.meta.requestStatus === 'fulfilled') {
        setLastSaved(new Date().toISOString());
      }
    },
    [clientId, dispatch, organizationId]
  );

  const debounceFn = useMemo(() => debounce(autosaveForm, 250), [autosaveForm]);

  useEffect(() => {
    return () => {
      debounceFn.cancel();
    };
  }, [debounceFn]);

  const handleSnapshotChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const clippedSnapshot = event.target.value.slice(0, 500);
    const values = setIn(formik.values, event.target.name, clippedSnapshot);

    formik.handleChange({
      ...event,
      target: { ...event.target, value: clippedSnapshot, name: event.target.name }
    });

    debounceFn(values);
  };

  return (
    <ModalPopup
      open={open}
      customClassInsideContainer={style.snapshotModal}
      customClassModalContentContainer={style.snapshotModalContent}
    >
      <div className={style.snapshotContent}>
        <div className={style.snapshotHeader}>
          <h3>Snapshot</h3>
          <button type="button" className={style.snapshotCloseButton} onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <Stack width="100%" height="128px">
            <TextAreaInputField
              onBlur={formik.handleBlur}
              onChange={handleSnapshotChange}
              name="snapshot"
              value={formik.values.snapshot}
              enableCounter
              maxLength={500}
              rowsNumber={4}
              placeholder="Type here"
              classnamesProps={style.snapshotTextArea}
              autoFocus
            />
          </Stack>
          <div className={style.snapshotAutosave}>
            {isAutosaving ? <LoadingSpinner size={20} /> : <LastSaved lastSaved={lastSaved} />}
          </div>
        </form>
      </div>
    </ModalPopup>
  );
}

type MpiNoteActionsProps = {
  clientId: string;
  visible?: boolean;
};

type OpenModal = 'screener' | 'snapshot' | null;

export const MpiNoteActions = ({ clientId, visible = true }: MpiNoteActionsProps) => {
  const { enableMpiUpdate2 } = useFlags();
  const dispatch = useAppDispatch();
  const formik = useFormikContext<NoteFormValues>();
  const openPreloadClientId = useRef<string | null>(null);
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [selectedScreener, setSelectedScreener] = useState<ScreenerTypeLowercase>('impact');
  const [isPreloading, setIsPreloading] = useState(false);
  const client = useAppSelector((state) => state.clients.client.data);
  const screeners = useAppSelector((state) => state.screener[clientId]?.screeners?.data);
  const shouldShowActions =
    Boolean(enableMpiUpdate2) && visible && isMpiNoteType(formik.values.noteType);

  useEffect(() => {
    if (!shouldShowActions || !clientId || openPreloadClientId.current === clientId) return;

    let isMounted = true;
    openPreloadClientId.current = clientId;
    setIsPreloading(true);
    const { organizationId } = getLsUserRoleObject();
    const preloadRequests = [
      dispatch(getScreenerDetails({ clientId, type: 'impact' })),
      dispatch(getScreenerHistory({ clientId, type: 'impact' })),
      dispatch(getScreeners({ clientId })),
      dispatch(getScreenerQuestions({ clientId, type: 'impact' })),
      dispatch(getOtherScreenerDetails({ clientId })),
      dispatch(getOtherScreenerHistory({ clientId }))
    ];

    if (organizationId && client?.id?.toString() !== clientId) {
      preloadRequests.push(dispatch(getClient({ organizationId, clientId, skipLoading: true })));
    }

    Promise.all(preloadRequests).finally(() => {
      if (isMounted) {
        setIsPreloading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [client?.id, clientId, dispatch, shouldShowActions]);

  const getInitialScreener = () => {
    const hasImpactScreener = screeners?.some((screener) => screener.type === 'IMPACT');
    const hasOtherScreener = screeners?.some((screener) => screener.type === 'OTHER');

    return hasImpactScreener && !hasOtherScreener ? 'other' : 'impact';
  };

  if (!shouldShowActions) {
    return null;
  }

  return (
    <>
      <div className={style.mpiActions}>
        <button
          className={style.mpiActionButton}
          type="button"
          disabled={isPreloading}
          onClick={() => {
            setSelectedScreener(getInitialScreener());
            setOpenModal('screener');
          }}
        >
          Screener
        </button>
        <button
          className={style.mpiActionButton}
          type="button"
          disabled={isPreloading}
          onClick={() => setOpenModal('snapshot')}
        >
          Snapshot
        </button>
      </div>
      <ScreenerModal
        clientId={clientId}
        open={openModal === 'screener'}
        selectedScreener={selectedScreener}
        onSelectScreener={setSelectedScreener}
        onClose={() => setOpenModal(null)}
      />
      <SnapshotModal
        clientId={clientId}
        open={openModal === 'snapshot'}
        onClose={() => setOpenModal(null)}
      />
    </>
  );
};
